import React, { useState } from 'react';
import { X, BookOpen, ExternalLink, ArrowRight, Wind, Mountain, Droplets, Flame, LayoutGrid, CheckCircle2, Circle, ChevronDown } from 'lucide-react';
import { KNOWLEDGE_BASE } from '../../utils/library';
import { THEMES } from '../../utils/constants';

export const EducationModal = ({ onClose, onNavigate, readArticles = [], onMarkRead }) => {
    const [activeFilter, setActiveFilter] = useState('all');

    // Filter Logic
    const filteredItems = activeFilter === 'all' 
        ? KNOWLEDGE_BASE 
        : KNOWLEDGE_BASE.filter(item => item.element === activeFilter);

    // Icon Helper
    const getIcon = (element) => {
        switch(element) {
            case 'air': return <Wind className="w-5 h-5" />;
            case 'earth': return <Mountain className="w-5 h-5" />;
            case 'water': return <Droplets className="w-5 h-5" />;
            case 'fire': return <Flame className="w-5 h-5" />;
            default: return <BookOpen className="w-5 h-5" />;
        }
    };

    const filters = [
        { id: 'all', label: 'All Topics', icon: LayoutGrid },
        { id: 'air', label: 'Vision (Air)', icon: Wind },
        { id: 'earth', label: 'Assets (Earth)', icon: Mountain },
        { id: 'water', label: 'Flow (Water)', icon: Droplets },
        { id: 'fire', label: 'Burn (Fire)', icon: Flame },
    ];

    // Progress Calculation
    const totalCount = KNOWLEDGE_BASE.length;
    const readCount = readArticles.length;
    const progress = Math.round((readCount / totalCount) * 100);

    return (
        <div className="fixed inset-0 z-[150] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-slate-950 border border-slate-800 w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
                
                {/* HEADER */}
                <div className="p-6 border-b border-slate-800 flex justify-between items-start bg-slate-900/50">
                    <div>
                        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                            <BookOpen className="w-8 h-8 text-indigo-400" /> 
                            Knowledge Base
                        </h2>
                        <div className="flex items-center gap-3 mt-1">
                            <p className="text-slate-400 text-sm">
                                Master the philosophy.
                            </p>
                            <span className="text-xs bg-slate-800 px-2 py-0.5 rounded-full text-indigo-300 border border-indigo-500/30">
                                {readCount}/{totalCount} Read
                            </span>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-full transition-colors text-slate-400 hover:text-white">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* FILTER BAR - Responsive */}
                <div className="px-6 py-4 border-b border-slate-800 bg-slate-950">
                    
                    {/* MOBILE: Dropdown (Stacking Experience) */}
                    <div className="md:hidden relative">
                        <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                        <select 
                            value={activeFilter}
                            onChange={(e) => setActiveFilter(e.target.value)}
                            className="w-full appearance-none bg-slate-900 border border-slate-700 text-white text-sm font-bold py-2.5 pl-4 pr-10 rounded-xl outline-none focus:border-indigo-500 transition-colors uppercase tracking-wide"
                        >
                            {filters.map(f => (
                                <option key={f.id} value={f.id}>{f.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* DESKTOP: Pill Row */}
                    <div className="hidden md:flex gap-2 overflow-x-auto scrollbar-hide">
                        {filters.map(f => {
                            const Icon = f.icon;
                            const isActive = activeFilter === f.id;
                            const theme = THEMES[f.id === 'all' ? 'slate' : f.id];
                            
                            return (
                                <button
                                    key={f.id}
                                    onClick={() => setActiveFilter(f.id)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap border ${
                                        isActive 
                                        ? `bg-white text-slate-950 border-white` 
                                        : `bg-slate-900 text-slate-400 border-slate-800 hover:border-slate-600 hover:text-white`
                                    }`}
                                >
                                    <Icon className={`w-3 h-3 ${isActive ? 'text-slate-900' : theme?.tailwind ? `text-${theme.tailwind}` : 'text-slate-400'}`} />
                                    {f.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* CONTENT GRID */}
                <div className="p-6 overflow-y-auto bg-slate-950">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {filteredItems.map((item) => {
                            const theme = THEMES[item.element];
                            const isRead = readArticles.includes(item.id);

                            return (
                                <div key={item.id} className={`group relative bg-slate-900 border rounded-xl p-5 transition-all hover:shadow-lg hover:shadow-${theme.tailwind}/10 flex flex-col ${isRead ? 'border-emerald-500/30 bg-slate-900/40' : 'border-slate-800 hover:border-' + theme.tailwind + '/50'}`}>
                                    
                                    {/* Card Header */}
                                    <div className="flex items-start gap-4 mb-3">
                                        <div className={`p-3 rounded-lg ${theme.bg} border ${theme.border} text-${theme.tailwind}`}>
                                            {getIcon(item.element)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start">
                                                <span className={`text-[10px] font-bold uppercase tracking-widest text-${theme.tailwind} mb-1 block`}>
                                                    {item.element} Element
                                                </span>
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); onMarkRead(item.id); }}
                                                    className={`transition-colors ${isRead ? 'text-emerald-500' : 'text-slate-600 hover:text-white'}`}
                                                    title={isRead ? "Read" : "Mark as Read"}
                                                >
                                                    {isRead ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                                                </button>
                                            </div>
                                            <h3 className={`text-lg font-bold leading-tight ${isRead ? 'text-slate-400' : 'text-white'}`}>
                                                {item.title}
                                            </h3>
                                        </div>
                                    </div>

                                    {/* Primer Text */}
                                    <p className={`text-sm leading-relaxed mb-6 flex-1 ${isRead ? 'text-slate-600' : 'text-slate-400'}`}>
                                        {item.primer}
                                    </p>

                                    {/* Actions */}
                                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-800">
                                        <button 
                                            onClick={() => { onNavigate(item.internalLink); onClose(); }}
                                            className="text-xs font-bold text-white flex items-center gap-1 hover:text-indigo-400 transition-colors"
                                        >
                                            Go to Tab <ArrowRight className="w-3 h-3" />
                                        </button>

                                        {item.externalResource && (
                                            <a 
                                                href={item.externalResource.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                onClick={() => onMarkRead(item.id)} // Auto-mark read on click
                                                className="text-xs flex items-center gap-1.5 text-slate-500 hover:text-white transition-colors bg-slate-950 px-3 py-1.5 rounded-lg border border-slate-800 hover:border-slate-600"
                                            >
                                                {item.externalResource.label} <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    
                    {filteredItems.length === 0 && (
                        <div className="text-center py-12 text-slate-500">
                            <p>No topics found for this filter.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};